import type { CampaignOperationLocale, CampaignOperationReason } from "@atsready/shared";

const en = {
  title: "Promotion email operations",
  history: "Email operation history",
  prerequisite: "Select a saved campaign to prepare an email operation.",
  permission: "Email operations require the ADMIN role.",
  emptyHistory: "No email operations yet.",
  action: "Operation",
  announce_grant: "Announce existing Pro+ access",
  marketing_offer: "Send a promotional offer",
  grant_pro_plus: "Grant Pro+ and notify",
  announceHelp:
    "Announce existing access using saved grant dates. No subscription changes. The maintained grant email supplies the subject and content.",
  offerHelp:
    "Offers respect communication preferences and the saved campaign. Published content is not translated; language mismatches are excluded.",
  grantHelp:
    "Grant eligible Free accounts Pro+ access, then notify them. No card required. Free after expiry unless the user chooses a paid plan. Saving a campaign does not grant access.",
  recipients: "Select recipients",
  search: "Search by name or email",
  selected: "Selected",
  selectionHelp:
    "Select 1–100 users explicitly. Eligibility is checked during preview and again before execution.",
  emptyUsers: "No users found.",
  loading: "Loading…",
  error: "Request failed. Reload the operation status before trying again.",
  uncertain:
    "The request timed out. Its outcome is uncertain. Durable status is being reloaded; review it before resuming.",
  rate: "Requests are paused until the retry period ends.",
  previous: "Previous page",
  next: "Next page",
  reload: "Reload",
  reason: "Internal reason",
  reasonHelp: "Required for the audit record; not included in the email.",
  duration: "Trial duration in days",
  durationHelp: "Enter 1–90 whole days. Independent of the signup trial policy.",
  locale: "Published content language",
  preview: "Preview selected audience",
  review: "Review operation",
  confirm: "Confirm and start",
  cancel: "Cancel",
  close: "Close",
  expires: "Review expires",
  expired: "This review expired. Preview again.",
  estimated: "Preview dates are estimates until a grant is saved.",
  eligible: "Eligible",
  excluded: "Excluded",
  sample: "Email preview",
  subject: "Subject",
  noEligible: "No eligible recipients. No grants or notifications will be created.",
  progress: "Operation status",
  grant: "Access grant",
  notification: "Notification",
  acceptedHelp: "Provider accepted does not confirm inbox delivery.",
  pendingHelp:
    "Pending recipients require Resume. Pausing stops future requests; an already submitted request may still complete.",
  resume: "Resume pending recipients",
  pause: "Pause after current recipient",
  retry: "Retry notification only",
  reconcile: "Reconcile uncertain notification",
  manual: "Needs manual review",
  providerPause:
    "Provider processing is paused. Review configuration or wait for the displayed retry time.",
  blockedUntil: "Retry after",
  not_requested: "Not requested",
  pending: "Pending",
  granted: "Granted",
  skipped: "Skipped",
  failed: "Failed",
  preparing: "Preparing",
  preparation_failed: "Preparation failed",
  sending: "Submitting",
  accepted: "Provider accepted",
  rejected: "Provider rejected",
  unknown: "Acceptance unknown",
  previewPages: "Preview recipient pages",
  nextPreview: "Next preview recipient page",
  previousPreview: "Previous preview recipient page",
  reloadPreview: "Reload preview recipients",
  detail: "Open operation",
  moreRecipients: "Next recipient page",
  start: "Starts",
  end: "Ends",
  savedCampaign: "Saved campaign",
};
type Copy = { [K in keyof typeof en]: string };
const de: Copy = {
  title: "Aktionen für Werbe-E-Mails",
  history: "Verlauf der E-Mail-Aktionen",
  prerequisite: "Wähle eine gespeicherte Kampagne, um eine E-Mail-Aktion vorzubereiten.",
  permission: "E-Mail-Aktionen erfordern die Rolle ADMIN.",
  emptyHistory: "Noch keine E-Mail-Aktionen.",
  action: "Aktion",
  announce_grant: "Bestehenden Pro+-Zugang ankündigen",
  marketing_offer: "Ein Werbeangebot senden",
  grant_pro_plus: "Pro+ gewähren und benachrichtigen",
  announceHelp:
    "Bestehenden Zugang mit gespeicherten Laufzeitdaten ankündigen. Keine Abonnementänderung. Betreff und Inhalt stammen aus der gepflegten Bestätigungsvorlage.",
  offerHelp:
    "Angebote beachten Kommunikationseinstellungen und die gespeicherte Kampagne. Veröffentlichte Inhalte werden nicht übersetzt; abweichende Sprachen werden ausgeschlossen.",
  grantHelp:
    "Berechtigten Free-Konten Pro+ gewähren und sie anschließend benachrichtigen. Keine Karte erforderlich. Nach Ablauf wieder Free, sofern kein kostenpflichtiger Tarif gewählt wird. Das Speichern einer Kampagne gewährt keinen Zugang.",
  recipients: "Empfänger auswählen",
  search: "Nach Name oder E-Mail suchen",
  selected: "Ausgewählt",
  selectionHelp:
    "1–100 Personen einzeln auswählen. Die Berechtigung wird bei der Vorschau und vor der Ausführung geprüft.",
  emptyUsers: "Keine Personen gefunden.",
  loading: "Wird geladen…",
  error: "Anfrage fehlgeschlagen. Lade den Aktionsstatus vor einem erneuten Versuch.",
  uncertain:
    "Die Anfrage hat das Zeitlimit überschritten. Das Ergebnis ist ungewiss. Der gespeicherte Status wird neu geladen; prüfe ihn vor dem Fortsetzen.",
  rate: "Anfragen sind bis zum Ende der Wartezeit pausiert.",
  previous: "Vorherige Seite",
  next: "Nächste Seite",
  reload: "Neu laden",
  reason: "Interne Begründung",
  reasonHelp: "Für das Prüfprotokoll erforderlich; nicht Teil der E-Mail.",
  duration: "Testdauer in Tagen",
  durationHelp: "1–90 ganze Tage eingeben. Unabhängig von der Testdauer bei der Registrierung.",
  locale: "Sprache veröffentlichter Inhalte",
  preview: "Ausgewählte Empfänger prüfen",
  review: "Aktion prüfen",
  confirm: "Bestätigen und starten",
  cancel: "Abbrechen",
  close: "Schließen",
  expires: "Prüfung gültig bis",
  expired: "Diese Prüfung ist abgelaufen. Erstelle eine neue Vorschau.",
  estimated: "Die Daten der Vorschau sind Schätzungen, bis der Zugang gespeichert ist.",
  eligible: "Berechtigt",
  excluded: "Ausgeschlossen",
  sample: "E-Mail-Vorschau",
  subject: "Betreff",
  noEligible:
    "Keine berechtigten Empfänger. Es werden weder Zugänge gewährt noch Benachrichtigungen erstellt.",
  progress: "Aktionsstatus",
  grant: "Zugang",
  notification: "Benachrichtigung",
  acceptedHelp: "Vom Anbieter angenommen bedeutet nicht im Postfach zugestellt.",
  pendingHelp:
    "Ausstehende Empfänger erfordern Fortsetzen. Pausieren stoppt künftige Anfragen; eine bereits übermittelte Anfrage kann noch abgeschlossen werden.",
  resume: "Ausstehende Empfänger fortsetzen",
  pause: "Nach aktuellem Empfänger pausieren",
  retry: "Nur Benachrichtigung erneut versuchen",
  reconcile: "Ungewisse Benachrichtigung abgleichen",
  manual: "Manuelle Prüfung erforderlich",
  providerPause:
    "Die Verarbeitung beim Anbieter ist pausiert. Prüfe die Konfiguration oder warte bis zum angegebenen Zeitpunkt.",
  blockedUntil: "Erneut versuchen nach",
  not_requested: "Nicht angefordert",
  pending: "Ausstehend",
  granted: "Gewährt",
  skipped: "Übersprungen",
  failed: "Fehlgeschlagen",
  preparing: "Wird vorbereitet",
  preparation_failed: "Vorbereitung fehlgeschlagen",
  sending: "Wird übermittelt",
  accepted: "Vom Anbieter angenommen",
  rejected: "Vom Anbieter abgelehnt",
  unknown: "Annahme ungewiss",
  previewPages: "Seiten der Empfängervorschau",
  nextPreview: "Nächste Seite der Empfängervorschau",
  previousPreview: "Vorherige Seite der Empfängervorschau",
  reloadPreview: "Empfängervorschau neu laden",
  detail: "Aktion öffnen",
  moreRecipients: "Nächste Empfängerseite",
  start: "Beginn",
  end: "Ende",
  savedCampaign: "Gespeicherte Kampagne",
};
export const campaignEmailCopy: Record<CampaignOperationLocale, Copy> = { en, de };
export function campaignEmailLocale(
  languages: readonly string[] = navigator.languages,
): CampaignOperationLocale {
  return languages[0]?.toLowerCase().startsWith("de") ? "de" : "en";
}
const reasons: Record<CampaignOperationReason, [string, string]> = {
  self: ["Your own account", "Eigenes Konto"],
  test_user: ["Test persona", "Testkonto"],
  user_missing: ["User no longer exists", "Person existiert nicht mehr"],
  subscription_missing: ["No subscription record", "Kein Abonnementdatensatz"],
  paid_account: ["Paid account", "Kostenpflichtiges Konto"],
  stripe_account: ["Stripe-managed account", "Von Stripe verwaltetes Konto"],
  invalid_status: ["Ineligible subscription status", "Nicht berechtigter Abonnementstatus"],
  prior_trial: ["Trial already used", "Testzugang bereits genutzt"],
  trial_already_present: ["Trial already present", "Testzugang bereits vorhanden"],
  no_active_grant: ["No active Pro+ grant", "Kein aktiver Pro+-Zugang"],
  unsubscribed: [
    "Communication preference excludes this email",
    "Kommunikationseinstellung schließt diese E-Mail aus",
  ],
  target_tier_mismatch: ["Outside campaign target tiers", "Außerhalb der Kampagnenzieltarife"],
  template_language_mismatch: [
    "Published content language does not match",
    "Sprache veröffentlichter Inhalte stimmt nicht überein",
  ],
  address_changed: ["Email address changed", "E-Mail-Adresse geändert"],
  grant_changed: ["Access changed since preview", "Zugang seit Vorschau geändert"],
  payload_expired: ["Prepared email expired", "Vorbereitete E-Mail abgelaufen"],
  preparation_failed: [
    "Email preparation failed; access is unchanged",
    "E-Mail-Vorbereitung fehlgeschlagen; Zugang unverändert",
  ],
  invalid_state: ["State requires investigation", "Status erfordert Prüfung"],
  provider_rejected: [
    "Provider rejected the notification",
    "Anbieter hat die Benachrichtigung abgelehnt",
  ],
  provider_unknown: ["Provider acceptance is uncertain", "Annahme beim Anbieter ungewiss"],
  manual_review_required: ["Needs manual review", "Manuelle Prüfung erforderlich"],
};
export function campaignEmailReason(
  reason: CampaignOperationReason,
  locale: CampaignOperationLocale,
) {
  return reasons[reason][locale === "de" ? 1 : 0];
}
export const campaignEmailButtonClass =
  "min-h-11 h-auto max-w-full whitespace-normal break-words px-4 py-2";
